/**
 * SchoolLoginPage - Separate login page per school
 * URL: /school/:tenantId/login
 * Supports teacher, student, and parent login
 */
import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import afterLogin from '@/lib/afterLogin';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { GraduationCap, Loader2, LogIn, UserCog, Users, BookOpen, Shield } from 'lucide-react';
import { useAuthStore } from '@/lib/store';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const USER_TYPES = [
  { id: 'admin', label: 'Admin', icon: Shield, color: 'rose' },
  { id: 'teacher', label: 'Teacher', icon: UserCog, color: 'indigo' },
  { id: 'student', label: 'Student', icon: BookOpen, color: 'emerald' },
  { id: 'parent', label: 'Parent', icon: Users, color: 'amber' },
];

const SchoolLoginPage = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuthStore();
  const initialType = ['admin', 'teacher', 'student', 'parent'].includes(searchParams.get('type'))
    ? searchParams.get('type') : 'teacher';
  const [userType, setUserType] = useState(initialType);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);

  const { data: tenant, isLoading: loadingTenant } = useQuery({
    queryKey: ['school-tenant-public', tenantId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/public`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!tenantId,
    retry: false,
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Please fill in all fields'); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/school/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, tenant_id: tenantId, user_type: userType, remember_me: rememberMe }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login failed');

      setAuth(data.access_token, data.user, userType);
      toast.success(`Welcome, ${data.user.name}!`);

      // Forced password change — applies to all school_users on first login
      // or after any password reset.
      if (data.must_change_password || data.user?.must_change_password) {
        navigate(`/school/${tenantId}/change-password`);
        return;
      }

      // Route to appropriate portal — delegates to /api/auth/landing which
      // honours any ?redirect=/... query param if safe and permitted.
      const redirect = new URLSearchParams(window.location.search).get('redirect') || '';
      await afterLogin({ navigate, userType, tenantId, redirect });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const brandColor = tenant?.primary_color || '#6366f1';

  if (loadingTenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4" data-testid="school-login-page">
      <div className="w-full max-w-md">
        {/* School Branding */}
        <div className="text-center mb-8">
          {tenant?.logo_url ? (
            <img
              src={tenant.logo_url.startsWith('http') ? tenant.logo_url : `${API_URL}${tenant.logo_url}`}
              alt={tenant?.name || 'School'}
              className="h-20 w-auto max-w-[160px] mx-auto mb-3 rounded-lg object-contain bg-white p-1 shadow-sm"
              data-testid="school-login-logo"
            />
          ) : (
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3"
              style={{ backgroundColor: brandColor }}
              data-testid="school-login-logo-fallback"
            >
              <GraduationCap className="h-8 w-8 text-white" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-slate-900">{tenant?.name || 'School Portal'}</h1>
          {tenant?.code && <p className="text-sm text-slate-500 mt-1">{tenant.code}</p>}
          <p className="text-xs text-slate-400 mt-2">Dedicated portal for this school</p>
        </div>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            {/* User Type Tabs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 p-1 bg-slate-100 rounded-lg mb-6">
              {USER_TYPES.map(t => (
                <button
                  key={t.id}
                  onClick={() => setUserType(t.id)}
                  className={`flex items-center justify-center gap-1 py-2 px-2 rounded-md text-xs font-medium transition-all ${
                    userType === t.id
                      ? 'bg-white shadow-sm text-slate-900'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid={`login-tab-${t.id}`}
                >
                  <t.icon className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{t.label}</span>
                  <span className="sm:hidden">{t.label.slice(0, 3)}</span>
                </button>
              ))}
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder={`${userType}@school.com`}
                  data-testid="school-login-email"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Password</Label>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!email) { toast.error('Enter your email first'); return; }
                      try {
                        const res = await fetch(`${API_URL}/api/school/auth/forgot-password`, {
                          method: 'POST', headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ tenant_id: tenantId, email, user_type: userType }),
                        });
                        await res.json();
                        toast.success('If the email is registered, a reset link has been sent.');
                      } catch { toast.error('Could not send reset link'); }
                    }}
                    className="text-xs text-indigo-600 hover:text-indigo-800 underline"
                    data-testid="school-forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
                <Input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  data-testid="school-login-password"
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-600 select-none cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300"
                  data-testid="school-remember-me"
                />
                Remember me for 30 days
              </label>
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                style={{ backgroundColor: brandColor }}
                data-testid="school-login-submit"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <LogIn className="h-4 w-4 mr-2" />}
                Sign In as {userType.charAt(0).toUpperCase() + userType.slice(1)}
              </Button>
            </form>

            <p className="text-xs text-slate-400 text-center mt-4">
              {userType === 'admin' && 'School admin or super-admin credentials'}
              {userType === 'student' && 'Default password: Your admission number'}
              {userType === 'teacher' && 'Contact admin if you forgot your password'}
              {userType === 'parent' && 'Default password: parent123'}
            </p>
          </CardContent>
        </Card>

        <p className="text-xs text-slate-400 text-center mt-4">
          <a href="/login" className="hover:underline">Back to main login</a>
        </p>
      </div>
    </div>
  );
};

export default SchoolLoginPage;
